import React from 'react';
import PropTypes from 'prop-types';

import {Box, TextField, Button, Typography, Stack, Paper} from 'views/components';
import {Translate, translate} from 'views/module';
import LoadingMessage from './LoadingMessage';
import {SendOutlined} from 'views/icons';

const ChatWindow = (props) => {
  const {sendMessage, messages, isLoading, isTyping} = props;

  const [input, setInput] = React.useState('');

  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [messages]);

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isLastBot = lastMessage && lastMessage.author === 'bot';
  const canSend = (!lastMessage) || isLastBot;

  const onSend = () => {
    if (!canSend || !input.trim()) return;
    sendMessage(input);
    setInput('');
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" position="relative">
      <Box flex={1} minHeight={0} display="flex" flexDirection="column" overflow="hidden">
        {
          isLoading &&
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
            sx={{backgroundColor: '#FFF'}}
          >
            <Typography variant="h6">
              <Translate expression="profile.ChatWindow.Loading" />...
            </Typography>
          </Box>
        }
        {
          !isLoading &&
        <Box
          flex={1}
          p={2}
          overflow="auto"
          sx={{backgroundColor: '#FFF'}}
        >
          <Stack spacing={2}>
            {
              messages.map((msg, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent={msg.author === 'user' ? 'flex-end' : 'flex-start'}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: '75%',
                      p: 1.5,
                      backgroundColor: msg.author === 'user' ? '#e0f7fa' : '#eeeeee',
                      borderRadius: '8px',
                    }}
                  >
                    {typeof msg.text === 'string' ? (
                      <Typography variant="body2">{msg.text}</Typography>
                    ) : (
                      msg.text
                    )}
                  </Paper>
                </Box>
              ))
            }
            {isTyping && (
              <Box display="flex" justifyContent="flex-start">
                <LoadingMessage />
              </Box>
            )}
          </Stack>
          <Box ref={bottomRef} />
        </Box>
        }
      </Box>

      {/* Textbox + Send */}
      <Box
        display="flex"
        gap={1}
        p={2}
        alignItems="center"
        sx={{backgroundColor: '#fff', borderTop: '1px solid #ccc'}}
      >
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          placeholder={translate('profile.ChatWindow.InputPlaceholder')}
          value={input}
          onChange={setInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSend && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          disabled={!canSend}
        />
        <Button
          variant="contained"
          sx={{backgroundColor: '#888', width: '40px', height: '40px'}}
          onClick={onSend}
          disabled={!canSend || !input.trim()}
        >
          <SendOutlined />
        </Button>
      </Box>
    </Box>
  );
};

ChatWindow.propTypes = {
  sendMessage: PropTypes.func.isRequired,
  messages: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isTyping: PropTypes.bool.isRequired,
};

export default ChatWindow;
